// TUT-448 — o cliente HTTP da web: a sessão e o CSRF, a perda de sessão, o `request` e o `requestBlob`, e o estado de demonstração que ainda mora fora do `lib/demo`.
import { ApiError } from "../api-error";
import { hojeLocal } from "../contas-a-pagar";
import { definirFusoDoEscritorio } from "../format";
import { reportarSuspeitaDeQueda } from "../maintenance";
import { limparJanela, registrarJanela } from "../maintenance-window";
import type { Session } from "../types";
import type { VenceDePrazo } from "../indicadores";
import { adoptThemePreference } from "../useThemePreference";
import { adoptMenuPreference } from "../useMenuPreference";

/** TUT-303 — o que o servidor responde em `GET /demo-data`. */
export interface DemoDataState {
  disponivel: boolean;
  motivo: string | null;
  carregado: boolean;
  linhas: number;
}
export const DEMO_DATA_NO_NAVEGADOR: DemoDataState = {
  disponivel: false,
  motivo: "Este ambiente já nasce com os dados de demonstração.",
  carregado: true,
  linhas: 0,
};
export let csrfToken = "";
export const demoLocalRepresentatives: import("../types").LocalRepresentative[] = [];
export const demoLocalRepresentativeFinancialTerms = new Map<string, import("../types").LocalRepresentativeFinancialTerms>();
/**
 * Uma data de N dias atrás, para as fixtures da demonstração (TUT-251).
 *
 * Gêmea de `demoDatar` em `demo-data.ts`, e pelo mesmo motivo que o dono nomeou
 * em 16/09: data absoluta em fixture de demonstração não envelhece — já nasce
 * errada. Aqui isso mordia de um jeito específico: o contador `ATRASADOS` do
 * Financeiro mede idade, e com data fixa de julho ele contaria "atrasado" para
 * sempre, sem que nada estivesse atrasado de verdade.
 */
export function demoHaDias(dias: number): string {
  const data = new Date();
  data.setHours(9, 0, 0, 0);
  data.setDate(data.getDate() - dias);
  return data.toISOString();
}
export let demoAccountsPayable: import("../types").AccountsPayable[] = [{
  id: "ap-demo-001", expertiseId: "exp-001", processId: "proc-001",
  processNumber: "0001234-56.2023.5.02.0064", representativeId: "e0000000-0000-4000-8000-000000000001",
  representativeName: "Dra. Mariana Costa",
  origin: "AUTOMATIC", description: null, notes: null, supplierId: null,
  payeeName: "Dra. Mariana Costa", payeeKind: "REPRESENTATIVE", contractedAmountCents: 85_000,
  excessKmAmountCents: 180, paymentTerms: "15 dias após os entregáveis",
  status: "AWAITING_DELIVERABLES", dueDate: null, deliverables: { technicalReport: null, invoiceReceipt: null },
  cancellationReason: null, paidAt: null, paidByName: null, cancelledAt: null,
  createdAt: demoHaDias(40), updatedAt: demoHaDias(40),
}, {
  // TUT-251 — as colunas "Programado" e "Pago" do quadro de DESPESAS INTERNAS
  // nasciam vazias por falta de fixture, e ninguém distinguiria isso das que
  // nascem vazias de propósito, à espera da cliente.
  id: "ap-demo-002", expertiseId: "exp-002", processId: "proc-002",
  processNumber: "0007654-32.2024.5.02.0011", representativeId: "e0000000-0000-4000-8000-000000000002",
  representativeName: "Dr. Paulo Rezende",
  origin: "AUTOMATIC", description: null, notes: null, supplierId: null,
  payeeName: "Dr. Paulo Rezende", payeeKind: "REPRESENTATIVE", contractedAmountCents: 120_000,
  excessKmAmountCents: 0, paymentTerms: "30 dias após a entrega",
  // TUT-334 — venceu há dois dias: a faixa "Vencidas" nasce com conta.
  status: "READY_FOR_PAYMENT", dueDate: demoDiaDaqui(-2), deliverables: { technicalReport: { id: "ape-002-r", documentVersionId: "ver-demo" }, invoiceReceipt: { id: "ape-002-n", originalName: "nf-2210.pdf" } },
  cancellationReason: null, paidAt: null, paidByName: null, cancelledAt: null,
  createdAt: demoHaDias(12), updatedAt: demoHaDias(3),
}, {
  id: "ap-demo-003", expertiseId: "exp-003", processId: "proc-003",
  processNumber: "0002468-10.2024.5.02.0033", representativeId: "e0000000-0000-4000-8000-000000000001",
  representativeName: "Dra. Mariana Costa",
  origin: "AUTOMATIC", description: null, notes: null, supplierId: null,
  payeeName: "Dra. Mariana Costa", payeeKind: "REPRESENTATIVE", contractedAmountCents: 95_000,
  excessKmAmountCents: 2_400, paymentTerms: "15 dias após os entregáveis",
  status: "PAID", dueDate: demoDiaDaqui(-6), deliverables: { technicalReport: { id: "ape-003-r", documentVersionId: "ver-demo" }, invoiceReceipt: { id: "ape-003-n", originalName: "recibo-0077.pdf" } },
  cancellationReason: null, paidAt: demoHaDias(5), paidByName: "Renata Silva", cancelledAt: null,
  createdAt: demoHaDias(30), updatedAt: demoHaDias(5),
}, {
  // TUT-334 — uma conta em cada faixa que faltava: vence em 7 dias, a vencer
  // e sem vencimento (pronta de antes da data existir).
  id: "ap-demo-004", expertiseId: "exp-004", processId: "proc-001",
  processNumber: "0001234-56.2023.5.02.0064", representativeId: "e0000000-0000-4000-8000-000000000002",
  representativeName: "Dr. Paulo Rezende",
  origin: "AUTOMATIC", description: null, notes: null, supplierId: null,
  payeeName: "Dr. Paulo Rezende", payeeKind: "REPRESENTATIVE", contractedAmountCents: 64_000,
  excessKmAmountCents: 150, paymentTerms: "15 dias após os entregáveis",
  status: "READY_FOR_PAYMENT", dueDate: demoDiaDaqui(4), deliverables: { technicalReport: { id: "ape-004-r", documentVersionId: "ver-demo" }, invoiceReceipt: { id: "ape-004-n", originalName: "nf-4471.pdf" } },
  cancellationReason: null, paidAt: null, paidByName: null, cancelledAt: null,
  createdAt: demoHaDias(9), updatedAt: demoHaDias(2),
}, {
  id: "ap-demo-005", expertiseId: "exp-005", processId: "proc-002",
  processNumber: "0007654-32.2024.5.02.0011", representativeId: "e0000000-0000-4000-8000-000000000001",
  representativeName: "Dra. Mariana Costa",
  origin: "AUTOMATIC", description: null, notes: null, supplierId: null,
  payeeName: "Dra. Mariana Costa", payeeKind: "REPRESENTATIVE", contractedAmountCents: 110_000,
  excessKmAmountCents: 200, paymentTerms: "30 dias após a entrega",
  status: "READY_FOR_PAYMENT", dueDate: demoDiaDaqui(20), deliverables: { technicalReport: { id: "ape-005-r", documentVersionId: "ver-demo" }, invoiceReceipt: { id: "ape-005-n", originalName: "recibo-0192.pdf" } },
  cancellationReason: null, paidAt: null, paidByName: null, cancelledAt: null,
  createdAt: demoHaDias(7), updatedAt: demoHaDias(1),
}, {
  id: "ap-demo-006", expertiseId: "exp-006", processId: "proc-003",
  processNumber: "0002468-10.2024.5.02.0033", representativeId: "e0000000-0000-4000-8000-000000000002",
  representativeName: "Dr. Paulo Rezende",
  origin: "AUTOMATIC", description: null, notes: null, supplierId: null,
  payeeName: "Dr. Paulo Rezende", payeeKind: "REPRESENTATIVE", contractedAmountCents: 72_500,
  excessKmAmountCents: 0, paymentTerms: "15 dias após os entregáveis",
  status: "READY_FOR_PAYMENT", dueDate: null, deliverables: { technicalReport: { id: "ape-006-r", documentVersionId: "ver-demo" }, invoiceReceipt: { id: "ape-006-n", originalName: "nf-3310.pdf" } },
  cancellationReason: null, paidAt: null, paidByName: null, cancelledAt: null,
  createdAt: demoHaDias(20), updatedAt: demoHaDias(10),
}];
/** TUT-334 — um dia de calendário relativo a hoje, `AAAA-MM-DD`. */
export function demoDiaDaqui(dias: number): string {
  const dia = new Date();
  dia.setDate(dia.getDate() + dias);
  return hojeLocal(dia);
}
export let demoAccountsReceivable: import("../types").AccountsReceivable[] = [{
  id: "ar-demo-001",
  contractId: "contract-demo-001",
  serviceOrderId: "os-demo-001",
  processId: "proc-001",
  processNumber: "0001234-56.2023.5.02.0064",
  origin: "AUTOMATIC",
  description: null,
  notes: null,
  organizationName: "Cliente demonstração",
  organizationId: "c0000000-0000-4000-8000-000000000001",
  stage: "REPORT",
  sourceKind: "REPORT",
  occurrence: 1,
  amountCents: 250_000,
  route: "DIRECT",
  requirements: {
    purchaseOrderIdentifier: false,
    platformEvidence: false,
    manualRelease: false,
  },
  purchaseOrderIdentifier: null,
  manualReleasedAt: null,
  status: "READY_TO_INVOICE",
  evidence: { platform: null, issuanceProof: null },
  invoicedAt: null,
  dueDate: null,
  receivedAt: null,
  receivedAmountCents: null,
  receiptOrigin: null,
  cancellationReason: null,
  cancelledAt: null,
  createdAt: demoHaDias(21),
  updatedAt: demoHaDias(21),
}, {
  id: "ar-demo-po-001",
  contractId: "contract-demo-001",
  serviceOrderId: "os-demo-001",
  processId: "proc-001",
  processNumber: "0001234-56.2023.5.02.0064",
  origin: "AUTOMATIC",
  description: null,
  notes: null,
  organizationName: "Cliente com pedido de compra",
  organizationId: "c0000000-0000-4000-8000-000000000002",
  stage: "IMPUGNATION",
  sourceKind: "IMPUGNATION",
  occurrence: 2,
  amountCents: 75_000,
  route: "PURCHASE_ORDER",
  requirements: {
    purchaseOrderIdentifier: true,
    platformEvidence: false,
    manualRelease: false,
  },
  purchaseOrderIdentifier: null,
  manualReleasedAt: null,
  status: "WAITING_REQUIREMENT",
  evidence: { platform: null, issuanceProof: null },
  invoicedAt: null,
  dueDate: null,
  receivedAt: null,
  receivedAmountCents: null,
  receiptOrigin: null,
  cancellationReason: null,
  cancelledAt: null,
  createdAt: demoHaDias(35),
  updatedAt: demoHaDias(35),
}, {
  // A diligência FATURADA: é ela que prova, na tela, que `INVOICED` cai em
  // "Em pagamento" em vez de sumir do quadro.
  id: "ar-demo-faturada",
  contractId: "contract-demo-001",
  serviceOrderId: "os-demo-002",
  processId: "proc-002",
  processNumber: "0007654-32.2024.5.02.0011",
  origin: "AUTOMATIC",
  description: null,
  notes: null,
  organizationName: "Logística Via Norte S.A.",
  organizationId: "c0000000-0000-4000-8000-000000000003",
  stage: "REPORT",
  sourceKind: "REPORT",
  occurrence: 1,
  amountCents: 310_000,
  route: "DIRECT",
  requirements: {
    purchaseOrderIdentifier: false,
    platformEvidence: false,
    manualRelease: false,
  },
  purchaseOrderIdentifier: null,
  manualReleasedAt: null,
  status: "INVOICED",
  evidence: { platform: null, issuanceProof: null },
  invoicedAt: demoHaDias(4),
  // TUT-506 — faturada e VENCIDA (o vencimento foi ontem): é ela que mostra
  // o selo "Vencida" na lista e o atraso no quadro.
  dueDate: demoDiaDaqui(-1),
  receivedAt: null,
  receivedAmountCents: null,
  receiptOrigin: null,
  cancellationReason: null,
  cancelledAt: null,
  createdAt: demoHaDias(18),
  updatedAt: demoHaDias(4),
}, {
  // TUT-506 — a RECEBIDA: é ela que enche a coluna PAGO do quadro e prova o
  // "Desfazer recebimento".
  id: "ar-demo-recebida",
  contractId: "contract-demo-001",
  serviceOrderId: "os-demo-002",
  processId: "proc-002",
  processNumber: "0007654-32.2024.5.02.0011",
  origin: "AUTOMATIC",
  description: null,
  notes: null,
  organizationName: "Logística Via Norte S.A.",
  organizationId: "c0000000-0000-4000-8000-000000000003",
  stage: "QUESTIONS",
  sourceKind: "QUESTIONS",
  occurrence: 1,
  amountCents: 180_000,
  route: "DIRECT",
  requirements: {
    purchaseOrderIdentifier: false,
    platformEvidence: false,
    manualRelease: false,
  },
  purchaseOrderIdentifier: null,
  manualReleasedAt: null,
  status: "RECEIVED",
  evidence: { platform: null, issuanceProof: { id: "ar-demo-recebida-nf", originalName: "nf-quesitos.pdf" } },
  invoicedAt: demoHaDias(12),
  dueDate: demoDiaDaqui(-2),
  receivedAt: demoHaDias(3),
  receivedAmountCents: 180_000,
  receiptOrigin: "MANUAL",
  cancellationReason: null,
  cancelledAt: null,
  createdAt: demoHaDias(30),
  updatedAt: demoHaDias(3),
}];
export function demoCoverageKey(city: string, state: string) {
  return `${state.trim().toUpperCase()}:${city
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase()}`;
}

/**
 * TUT-181 fatia 2b — a fatia pedida, escrita UMA vez neste lado também.
 *
 * A 2a deixou duas cópias inline de `{ page?: number; pageSize?: number }`, e a
 * 2b levaria isso a onze. É o mesmo argumento que criou `PaginacaoOpcional` na
 * API: onze cópias são onze chances de uma divergir, e divergência de contrato
 * de lista não aparece em teste — aparece na tela, com a paginação sumindo.
 */
export interface FatiaPedida {
  page?: number;
  pageSize?: number;
  /**
   * TUT-204c — a ordenação viaja no MESMO objeto da paginação, e isso é
   * decisão de desenho, não conveniência.
   *
   * As duas respondem à mesma pergunta ("que recorte do conjunto eu quero?"),
   * viajam na mesma query string e são lidas pela mesma borda no servidor
   * (`fatiaPedida` + `ordenacaoPedida`). Separá-las obrigaria a acrescentar um
   * quinto parâmetro posicional a cada método de lista — e são dezesseis
   * listas, o que faria a próxima opção comum custar dezesseis assinaturas.
   *
   * A coluna é `string` e não a união fechada de propósito: `FatiaPedida` é
   * comum às dezesseis listas, e cada uma tem colunas próprias. Quem valida é
   * o `validateSearch` da rota, que sabe QUAL lista é — aqui a checagem seria
   * contra a união de tudo, que aceitaria `cidade` em `/peritos`.
   */
  sort?: string;
  dir?: "asc" | "desc";
}
export interface DeadlineListQuery extends FatiaPedida {
  search?: string;
  status?: string;  /** TUT-204j — o recorte por tipo e por responsável, aplicado no SERVIDOR. */
  type?: string;
  assignedUserId?: string;
  /**
   * TUT-245 — o assunto e o cliente, do PROCESSO, aplicados no SERVIDOR.
   *
   * A Agenda mistura prazo e perícia, e os dois filtros recortam as duas
   * fontes com os MESMOS parâmetros; ver `ExpertiseListQuery`.
   */
  subjectId?: string;
  clientOrganizationId?: string;
  /** TUT-475 — a faixa de vencimento do widget, cortada no SERVIDOR. */
  vence?: VenceDePrazo;
  /** TUT-475 — a janela com que o widget contou; ausente, vale a do escritório. */
  dias?: number;
  /**
   * TUT-476 — o período de VENCIMENTO (Série temporal) e o de CONCLUSÃO com
   * `noPrazo` (Medição): dias `AAAA-MM-DD`, inclusivos, no fuso do escritório.
   */
  periodoDe?: string;
  periodoAte?: string;
  concluidoDe?: string;
  concluidoAte?: string;
  noPrazo?: boolean;
}
export interface ProposalListQuery extends FatiaPedida {
  search?: string;
  /**
   * TUT-488 — o período do quadro, em dias do escritório (`AAAA-MM-DD`),
   * extremos incluídos: "criada em" e "enviada em". O servidor filtra; a
   * proposta sem envio registrado não entra no "enviada em".
   */
  createdFrom?: string;
  createdTo?: string;
  sentFrom?: string;
  sentTo?: string;
  /**
   * TUT-584 — os filtros avançados da lista (a chave se repete): "ou" dentro do
   * campo, "e" entre campos. `modalidade` é a que a coluna mostra; `cliente` é
   * o id da organização.
   */
  status?: string[];
  modalidade?: string[];
  cliente?: string[];
}
/**
 * TUT-204c — a fila pericial ganhou nome, como `DeadlineListQuery` já tinha.
 *
 * Ela era um objeto ANÔNIMO escrito na assinatura do método, e por isso não
 * herdava `FatiaPedida`: montava `page`/`pageSize` à mão e ficaria de fora da
 * ordenação. Quarto caso desta fatia em que a forma da fatia aparece COPIADA em
 * vez de herdada — os outros três foram todos em Clientes. Dar nome ao filtro é
 * o que põe a próxima opção comum nas dezesseis listas de uma vez, e é a mesma
 * lição que a TUT-181 fatia 2b já tinha escrito para `SpecialWorkListFilters`.
 */
export interface ExpertiseListQuery extends FatiaPedida {
  search?: string;
  /** TUT-181 — repete na URL (`status=a&status=b`), como subjectId. */
  statuses?: string[];
  /**
   * TUT-245 — os três filtros da Agenda, aplicados no SERVIDOR.
   *
   * `subjectId` é o assunto do PROCESSO (catálogo CNJ) e NÃO a especialidade
   * da perícia — foi a palavra que a cliente escreveu, e a especialidade segue
   * visível no cartão. `location` casa por trecho no endereço ou na cidade.
   */
  subjectId?: string;
  clientOrganizationId?: string;
  location?: string;
  /** TUT-309 — `sem` é o "Pendente assistente"; `com`, o "Aguardando perícia". */
  assistente?: "sem" | "com";
  /** TUT-526 — o roteiro: `pendente` é o "Pendente formulário"; `completo`, o "Aguardando perícia". */
  formulario?: "pendente" | "completo";
  /** TUT-527 (D32) — "Atribuídas a mim": as perícias de que quem pede é o assistente. */
  mine?: boolean;
  /**
   * TUT-476 — o motivo de encerramento (`com` / `sem` motivo estruturado, ou o
   * id de um motivo) e o período de AGENDAMENTO (dias `AAAA-MM-DD`).
   */
  motivo?: string;
  periodoDe?: string;
  periodoAte?: string;
}
/**
 * Acrescenta a fatia à query e devolve `?...` (ou vazio, se nada foi pedido).
 *
 * **Só escreve o que veio**, e isso é contrato: sem `page`/`pageSize` a API
 * devolve o conjunto INTEIRO. Mandar `page=undefined` viraria a string
 * `"undefined"` na URL e a validação recusaria a requisição inteira.
 */
export function comFatia(params: URLSearchParams, fatia: FatiaPedida): string {
  if (fatia.page) params.set("page", String(fatia.page));
  if (fatia.pageSize) params.set("pageSize", String(fatia.pageSize));
  // TUT-204c: mesma regra do `page` — só escreve o que veio. Sem `sort`, a API
  // aplica o padrão da lista; um `sort=undefined` na URL viraria a string
  // "undefined" e o `@IsIn` do DTO recusaria a requisição inteira.
  if (fatia.sort) params.set("sort", fatia.sort);
  if (fatia.dir) params.set("dir", fatia.dir);
  return params.size ? `?${params}` : "";
}
/**
 * TUT-385 — sessão que cai no meio do uso volta ao login.
 *
 * Até aqui um 401 de qualquer chamada virava só uma mensagem de erro na tela:
 * a sessão tinha expirado (ou sido encerrada em outro lugar) e a pessoa seguia
 * olhando telas que já não carregavam, sem caminho de volta além de recarregar.
 * O portão da sessão (`TenantGate`) registra aqui o que fazer quando isso
 * acontece. O login responde 401 sem sessão nenhuma em jogo (credencial
 * errada) e fica de fora; a senha atual errada na troca de senha responde 400
 * desde esta fatia, justamente para não cair aqui.
 *
 * O aviso só vale para a sessão em que a requisição SAIU: uma resposta lenta
 * que volta 401 depois de a pessoa já ter entrado de novo não pode derrubar a
 * sessão nova. O token CSRF é a identidade da sessão desta aba — muda a cada
 * login (`captureSession`) —, e é ele que a requisição leva de lembrança.
 */
export let aoPerderSessao: (() => void) | null = null;
export const RESPONDEM_401_COM_SESSAO_VIVA = ["/auth/login"];
export function definirAoPerderSessao(acao: () => void): () => void {
  aoPerderSessao = acao;
  return () => {
    if (aoPerderSessao === acao) aoPerderSessao = null;
  };
}
/**
 * TUT-406 — token CSRF velho não perde a ação.
 *
 * O token desta aba é o da sessão em que ela entrou. Se a pessoa entra de novo
 * em OUTRA aba, o cookie da sessão muda para todas, e o token guardado aqui
 * deixa de valer: toda escrita desta aba tomava 403 "Validação CSRF falhou."
 * até recarregar, e a pessoa perdia o que tinha digitado sem entender por quê.
 *
 * Agora a escrita recusada por CSRF pergunta ao servidor qual é a sessão viva:
 * se é a MESMA pessoa no MESMO escritório, adota o token novo e repete uma vez.
 * Se a outra aba entrou em outro escritório (ou como outra pessoa), NÃO repete —
 * a ação iria para o escritório errado — e recarrega a página, que passa a
 * mostrar a sessão que de fato vale.
 */
export const RECUSA_DE_CSRF = "Validação CSRF falhou.";
export let donoDaSessao = "";
export function dono(session: Session): string {
  return `${session.tenant.id}/${session.user.id}`;
}
export function recarregarPagina() {
  window.location.reload();
}
export const recarregar = { pagina: recarregarPagina };
export async function ressincronizarCsrf(): Promise<boolean> {
  const anterior = donoDaSessao;
  let viva: Session;
  try {
    viva = await request<Session>("/auth/session");
  } catch {
    return false;
  }
  if (anterior && dono(viva) !== anterior) {
    recarregar.pagina();
    return false;
  }
  captureSession(viva);
  return true;
}
export async function request<T>(
  path: string,
  init: RequestInit = {},
  repetida = false,
): Promise<T> {
  const sessaoDaSaida = csrfToken;
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (csrfToken && init.method && init.method !== "GET")
    headers.set("X-CSRF-Token", csrfToken);

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
  } catch (error) {
    // Falha de rede: ou a conexão do usuário caiu, ou a API está fora
    // (redeploy). O módulo de manutenção confirma contra /api/health antes de
    // trocar a tela — aqui só se levanta a suspeita, sem segurar a chamada.
    void reportarSuspeitaDeQueda();
    throw error;
  }

  if (!response.ok) {
    // 502/503/504 vêm do proxy quando a API não responde — a assinatura de um
    // redeploy em andamento. Erros da própria API (4xx/500) não entram: defeito
    // de um endpoint não é manutenção, e chamar de manutenção o esconderia.
    if ([502, 503, 504].includes(response.status))
      void reportarSuspeitaDeQueda();
    const body = (await response.json().catch(() => null)) as {
      message?: string;
      code?: string;
    } | null;
    // TUT-145 fatia 1e: a guarda de manutenção DELIBERADA responde 503 com o
    // nível, o início e a previsão no corpo. Registrar aqui é o que faz o aviso
    // aparecer sem uma segunda chamada — que, no nível `FULL`, seria bloqueada
    // também, deixando o aviso mudo justamente quando ele mais importa.
    //
    // As duas manutenções não colidem: a sonda `/api/health` é isenta do
    // bloqueio, então o `reportarSuspeitaDeQueda()` acima confirma que a API
    // está viva e NÃO acende a tela reativa da TUT-121.
    if (response.status === 503) registrarJanela(body);
    if (
      response.status === 403 &&
      body?.message === RECUSA_DE_CSRF &&
      !repetida &&
      csrfToken === sessaoDaSaida &&
      (await ressincronizarCsrf())
    )
      return request<T>(path, init, true);
    if (
      response.status === 401 &&
      csrfToken === sessaoDaSaida &&
      !RESPONDEM_401_COM_SESSAO_VIVA.some((rota) => path.startsWith(rota))
    )
      aoPerderSessao?.();
    throw new ApiError(
      body?.message ?? "Não foi possível concluir a solicitação.",
      response.status,
      typeof body?.code === "string" ? body.code : undefined,
    );
  }

  // Uma resposta que passou é a prova de que a janela acabou — para ESTA
  // superfície. Some por fato, não por tempo: sumir por tempo faria o aviso
  // desaparecer com a manutenção ainda ativa, e a pessoa voltaria a tentar
  // salvar, que é o que ele existe para evitar.
  limparJanela();

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
export async function requestBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/pdf");
  if (csrfToken && init.method && init.method !== "GET") {
    headers.set("X-CSRF-Token", csrfToken);
  }
  const response = await fetch(`/api${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new ApiError(
      body?.message ?? "Não foi possível concluir a solicitação.",
      response.status,
    );
  }
  return response.blob();
}
/**
 * TUT-489c — um arquivo que volta de um POST com corpo JSON (o .zip do lote),
 * com os cabeçalhos da resposta (a contagem do que entrou e do que ficou de
 * fora). O `requestBlob` fica para o PDF da proposta, que não manda corpo.
 */
export async function requestArquivo(
  path: string,
  corpo: unknown,
  aceita: string,
): Promise<{ arquivo: Blob; cabecalhos: Headers }> {
  const headers = new Headers({ Accept: aceita, "Content-Type": "application/json" });
  if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
  const response = await fetch(`/api${path}`, {
    method: "POST",
    body: JSON.stringify(corpo),
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new ApiError(
      body?.message ?? "Não foi possível concluir a solicitação.",
      response.status,
    );
  }
  return { arquivo: await response.blob(), cabecalhos: response.headers };
}
/** O token e o dono desta aba pertencem a uma sessão que acabou. */
export function esquecerSessao(): void {
  csrfToken = "";
  donoDaSessao = "";
}
export function captureSession(session: Session): Session {
  csrfToken = session.csrfToken;
  donoDaSessao = dono(session);
  // TUT-153: a preferência de tema durável chega com a sessão — adotar aqui
  // faz a cor te seguir para qualquer dispositivo logo na primeira tela. O
  // localStorage continua pintando a PRIMEIRA tela antes da resposta chegar.
  if (session.user.themePreference)
    adoptThemePreference(session.user.themePreference);
  // TUT-465: o jeito do menu lateral segue a pessoa pelo mesmo caminho.
  if (session.user.menuPreference) adoptMenuPreference(session.user.menuPreference);
  // TUT-138a: o fuso do escritório chega junto da sessão e passa a valer para
  // TODA data renderizada. Fica aqui, e não na tela, porque este é o ponto
  // único por onde login e revalidação passam — espalhar a chamada faria
  // alguma rota esquecer e mostrar horário no fuso do navegador.
  definirFusoDoEscritorio(session.tenant.timeZone);
  return session;
}
export async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}
